import { InvoicePlan, InvoicePlanStatus } from '@wonderschool/common-base-types';
import { ColumnConfig, DataTable, SortByEnum } from '@wonderschool/common-base-ui';
import { t } from 'i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { useMemo } from 'react';
import { userHasPermission } from '../../../../api/firebase/account';
import { DefaultDateFormat } from '../../../../helpers/dates';
import { isRecurringPlanEligibleForArchive } from '../../../../helpers/invoicePlans';
import { currencyFormatter } from '../../../../helpers/utils';
import DateTime from '../../../DateTime/DateTime';
import { getStudentNames } from './InvoicePlanDetailSlideover';
import { InvoicePlanStatusLabel } from './InvoicePlanStatusLabel';
import { hasScheduledPlanEnd } from './endInvoicePlan';

export type InvoicePlansDataTableProps = {
  data: InvoicePlan[];
  onClick: (invoicePlan: InvoicePlan) => void;
  onEdit: (invoicePlan: InvoicePlan) => void;
  onEndPlan: (invoicePlan: InvoicePlan) => void;
  onChangeSelected: (selectedPlans: InvoicePlan[]) => void;
};
export function InvoicePlansDataTable({
  data,
  onClick,
  onEdit,
  onEndPlan,
  onChangeSelected,
}: InvoicePlansDataTableProps) {
  const { isEndBillingPlanEnabled } = useFlags();
  const canEditBilling = userHasPermission('can_edit_billing');
  const columns: ColumnConfig<InvoicePlan>[] = useMemo(
    () => [
      {
        fieldName: 'student.displayName',
        label: t('billing.invoicePlans.table.studentsColumn'),
        sortBy: SortByEnum.CLIENT,
        sortFunction: (a, b) => {
          if (a.students.length === 0 || b.students.length === 0) {
            return 0;
          }
          const aNames = getStudentNames(a.students);
          const bNames = getStudentNames(b.students);
          return aNames.localeCompare(bNames);
        },
        renderCell: (invoicePlan: InvoicePlan, index?: number) => {
          const isArchived = invoicePlan.isArchived;
          return (
            <>
              <div className="flex max-w-24 flex-wrap" data-testid={`recurring-plan-student-names-${index}`}>
                {getStudentNames(invoicePlan.students)}
              </div>
              {isArchived ? (
                <div data-testid={`recurring-plan-archived-label-${index}`} className="invoice-archived-label">
                  {t('recurringPlanList.archived.text')}
                </div>
              ) : (
                ''
              )}
            </>
          );
        },
      },
      {
        fieldName: 'billingInterval',
        label: t('billing.invoicePlans.table.billingIntervalColumn'),
        sortBy: SortByEnum.CLIENT,
        renderCell: (invoicePlan: InvoicePlan, index?: number) => (
          <div data-testid={`recurring-plan-billing-interval-${index}`}>{t(invoicePlan.billingInterval)}</div>
        ),
      },
      {
        fieldName: 'invoiceItemList',
        label: t('billing.invoicePlans.table.descriptionColumn'),
        renderCell: (invoicePlan: InvoicePlan, index?: number) => (
          <span data-testid={`recurring-plan-description-column-${index}`}>
            {invoicePlan.invoiceItemList
              .map((c) => {
                return t(c.item || c.description || '');
              })
              .join(', ')}
          </span>
        ),
      },
      {
        fieldName: 'total',
        label: t('billing.invoicePlans.table.amountColumn'),
        sortBy: SortByEnum.CLIENT,
        renderCell: (invoicePlan: InvoicePlan, index?: number) => (
          <span data-testid={`recurring-plan-total-amount-${index}`}>
            {currencyFormatter(invoicePlan.total, { precision: 2 })}
          </span>
        ),
      },
      {
        fieldName: 'dateSend',
        label: t('billing.invoicePlans.table.nextInvoiceDateColumn'),
        sortBy: SortByEnum.CLIENT,
        renderCell: (invoicePlan: InvoicePlan, index?: number) =>
          invoicePlan.dateSend && (!invoicePlan.dateEnd || moment(invoicePlan.dateEnd).isAfter(invoicePlan.dateDue)) ? (
            <span data-testid={`recurring-plan-send-date-${index}`}>
              <DateTime epoch={invoicePlan.dateSend} format={DefaultDateFormat} />
            </span>
          ) : (
            'n/a'
          ),
      },
      {
        fieldName: 'dateDue',
        label: t('billing.invoicePlans.table.nextDueDateColumn'),
        sortBy: SortByEnum.CLIENT,
        renderCell: (invoicePlan: InvoicePlan, index?: number) =>
          invoicePlan.dateDue && (!invoicePlan.dateEnd || moment(invoicePlan.dateEnd).isAfter(invoicePlan.dateDue)) ? (
            <span data-testid={`recurring-plan-due-date-${index}`}>
              <DateTime epoch={invoicePlan.dateDue} format={DefaultDateFormat} />
            </span>
          ) : (
            'n/a'
          ),
      },
      {
        fieldName: 'status',
        label: t('common.status'),
        sortBy: SortByEnum.CLIENT,
        renderCell: (invoicePlan: InvoicePlan, index?: number) => {
          const hasScheduledEnd = hasScheduledPlanEnd(invoicePlan);
          const notificationFailure = (
            invoicePlan as InvoicePlan & { endPlanNotificationFailure?: { message?: string } | null }
          ).endPlanNotificationFailure;
          return (
            <div className="space-y-1" data-testid={`recurring-plan-status-${index}`}>
              <InvoicePlanStatusLabel status={invoicePlan.status} />
              {hasScheduledEnd && (
                <p className="whitespace-nowrap text-xs font-medium text-gray-700">
                  {t('recurringPlanList.endPlan.endsOn', { date: '' })}{' '}
                  <DateTime
                    epoch={
                      (invoicePlan as InvoicePlan & { unenrollmentDate?: number | null }).unenrollmentDate ??
                      invoicePlan.dateEnd
                    }
                    format={DefaultDateFormat}
                  />
                </p>
              )}
              {notificationFailure && (
                <p className="max-w-40 text-xs font-medium text-red-700" data-testid={`end-plan-email-failed-${index}`}>
                  {t('recurringPlanList.endPlan.emailFailed')}
                </p>
              )}
            </div>
          );
        },
      },
      {
        fieldName: 'actions',
        label: '', // we don't want a header for this column
        renderCell: (invoicePlan: InvoicePlan, rowIndex?: number) => {
          const canEndPlan =
            isEndBillingPlanEnabled &&
            canEditBilling &&
            !invoicePlan.isArchived &&
            (invoicePlan.status === InvoicePlanStatus.ACTIVE || invoicePlan.status === InvoicePlanStatus.PAUSED);

          return (
            <div className="flex items-center justify-end gap-3 whitespace-nowrap px-2">
              <button
                type="button"
                data-testid={`recurring-plan-edit-link-${rowIndex}`}
                onClick={() => onEdit(invoicePlan)}
                className="cursor-pointer border-0 bg-transparent p-0 text-blue-900 hover:underline"
              >
                {t('common.edit')}
              </button>
              {canEndPlan && (
                <button
                  type="button"
                  data-testid={`recurring-plan-end-button-${rowIndex}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEndPlan(invoicePlan);
                  }}
                  className="font-medium text-red-700 hover:text-red-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"
                >
                  {t(
                    hasScheduledPlanEnd(invoicePlan)
                      ? 'recurringPlanList.endPlan.changeEndDate'
                      : 'recurringPlanList.endPlan.action'
                  )}
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [canEditBilling, isEndBillingPlanEnabled, onEdit, onEndPlan]
  );

  const handleChangedSelectedRows = (selectedRows: InvoicePlan[]) => {
    onChangeSelected(selectedRows);
  };

  return (
    <DataTable
      columns={columns}
      data={data}
      onClickRow={onClick}
      canSelect
      canSelectAll
      onChangeSelectedRows={handleChangedSelectedRows}
      isSelectionDisabled={(row) => !isRecurringPlanEligibleForArchive(row)}
    />
  );
}
